import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../helpers/api";
import { ShowToast } from "../../../components/toast";
import { ListResponse } from "../types";

export const useOnboardingList = (params: {
  status?: string;
  pmsType?: string;
  search?: string;
  setupMode?: "self_serve" | "concierge" | "null";
  page: number;
  limit: number;
}) => {
  return useQuery<ListResponse>({
    queryKey: [
      "sem-provisioning",
      "list",
      params.status ?? "",
      params.pmsType ?? "",
      params.search ?? "",
      params.setupMode ?? "",
      params.page,
      params.limit,
    ],
    queryFn: () => api.semProvisioning.list(params),
    refetchOnWindowFocus: false,
    // Auto-refresh while any user is actively provisioning. Cheap guard: poll
    // only when the current page contains a non-terminal row. Stop polling
    // once everything has reached a terminal (or awaiting-manual) state.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasActive = data.data.some(
        (u) =>
          u.overallStatus === "IN_PROGRESS" || u.overallStatus === "NOT_STARTED",
      );
      return hasActive ? 10_000 : false;
    },
  });
};

export const useMarkComplete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, step }: { userId: string; step: string }) =>
      api.semProvisioning.complete(userId, step),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Step marked complete" });
      queryClient.invalidateQueries({ queryKey: ["sem-provisioning"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message ?? "Failed to mark step complete",
      });
    },
  });
};

export const useRetryStep = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, step }: { userId: string; step: string }) =>
      api.semProvisioning.retry(userId, step),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Step reset and pipeline re-triggered",
      });
      queryClient.invalidateQueries({ queryKey: ["sem-provisioning"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message: error.response?.data?.message ?? "Failed to retry step",
      });
    },
  });
};

export const useRefreshBilling = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.semProvisioning.refreshBilling(userId),
    onSuccess: (data: {
      status: "completed" | "awaiting_manual" | "no_customer";
      message: string;
    }) => {
      if (data.status === "completed") {
        ShowToast({ type: "success", message: data.message });
      } else {
        ShowToast({ type: "error", message: data.message });
      }
      queryClient.invalidateQueries({ queryKey: ["sem-provisioning"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message ?? "Failed to refresh billing",
      });
    },
  });
};


export const useConfirmTracking = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.semProvisioning.confirmTracking(userId),
    onSuccess: (data: {
      confirmed: boolean;
      selfServeVerifiedAt: string;
      emailed: boolean;
    }) => {
      ShowToast({
        type: "success",
        message: `Tracking confirmed — go-live email ${
          data.emailed ? "sent" : "skipped (suppressed in non-prod)"
        }`,
      });
      queryClient.invalidateQueries({ queryKey: ["sem-provisioning"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message ?? "Failed to confirm tracking",
      });
    },
  });
};

export const useConfirmTrackingEmailPreview = (
  userId: string,
  enabled: boolean,
) => {
  return useQuery<{ subject: string; html: string }>({
    queryKey: ["confirm-tracking-email-preview", userId],
    queryFn: () => api.semProvisioning.previewConfirmTrackingEmail(userId),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useTriggerProvisioning = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.semProvisioning.trigger(userId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Pipeline triggered" });
      queryClient.invalidateQueries({ queryKey: ["sem-provisioning"] });
    },
    onError: (error: any) => {
      ShowToast({
        type: "error",
        message:
          error.response?.data?.message ?? "Failed to trigger pipeline",
      });
    },
  });
};
